import { AxiosInstance } from "axios"
import { Guid, Integerlike } from "src/interfaces/InleagueApiV1"

export type Selectable = {
  name: string,
  label: string,
  /**
   * Group and displayGroup are related to where the selectable option itself appears in the UI;
   * e.g. the checkbox for the selectable is grouped with other checkboxes of a similar sort.
   *
   * Unrelated but similar-seeming is `columnGroup` and `columnGroupPosition`
   *
   * Group currently serves as both group name and groupID
   * e.g. a group of "Some group name" has a UI label of "Some group name" and is uniquely keyed on "Some group name"
   * A falsy `group` property means "default" group.
   */
  group?: string,
  /**
   * Nested, ordinal indexed groupings within the "parent" grouping
   * If undefined, we treat it as if it were 1 ("the first grouping in the parent group")
   */
  displayGroup?: number,
  colDataType: "string" | "booleanish"
  selected?: boolean,
  argsDef?: Selectable_ArgDef[],
  tooltip?: string,
  /**
   * temporarily, this may be true, under the following conditions:
   *  - backend does not yet have a v4 resolver for a v3 target
   */
  __dev__disabled?: boolean,
  /**
   * columnGroup and columnGroupPosition are the "default" indices for resulting selects for this selectable.
   * This _should_ be defined for every selectable, but if it isn't, then code should default it to -1.
   */
  columnGroup?: Integerlike,
  columnGroupPosition?: Integerlike,
}

export interface Filterable {
  /**
   * temporarily, this may be true, under the following conditions:
   *  - backend does not yet have a v4 resolver for a v3 target
   */
    __dev__disabled?: boolean,
  /**
   * we had types like "boolean" and "text-op", but we ended up translating definitions like that
   * into functionlikes. Conceptually, the backend might define an ARGUMENT to be a particular type, like a boolean or etc.,
   * but that would be translated into a form type (probably a checkbox, in the boolean case), and then the frontend is not
   * aware of what "type" a filterable or its arguments are.
   */
  type: "functionlike",
  name: string,
  label: string,
  argsDef: Filterable_ArgDef[],
  /**
   * A javascript snippet used to drive a "toString" operation for rendering out a informational nodes in the UI.
   * Code is always inleague controlled; this is not, and must not be, driven by user input;
   * it always comes from our trusted backend.
   */
  jsToStringExpr?: string,
  /**
   * If `jsToStringExpr` is present, this is the executable version of that.
   * n.b. not an override of `toString`
   */
  getAsString?: (filterable: Filterable, args: ClientFuncArgBag) => string,
  tooltip?: string,
}

export type Selectable_ArgDef = {
  name: string,
  label: string,
  type: ArgDefType,
  // present for "select"
  options?: RawArgDefOptions
}

export type RawArgDefOptions_Shared = {type: "shared", name: string}
export type RawArgDefOptions_List = {label: string, value: string}[]
export type RawArgDefOptions_Tree = {
  type: "tree",
  levels: {label: string, name: string}[]
  /**
   * root level children is not optional
   */
  children: RawArgDefOptions_TreeNode[]
}

export type RawArgDefOptions_TreeNode = {
  data: {label: string, value: string},
  /**
   * If empty or absent, then it's a leaf
   */
  children?: RawArgDefOptions_TreeNode[]
}

export type RawArgDefOptions =
  | RawArgDefOptions_List
  | RawArgDefOptions_Shared
  | RawArgDefOptions_Tree

export type ArgDefType =
  | "number"
  | "select"
  | "select-many"
  | "menu-tree"
  | "text"
  | "date"
  | "checkbox"
  | "radio"

export type Filterable_ArgDef = {
  name: string,
  label?: string,
  type: ArgDefType,
  // present for "select"
  options?: RawArgDefOptions,
  /**
   * A javascript snippet used to drive visibility.
   * Code is always inleague controlled; this is not, and must not be, driven by user input;
   * it always comes from our trusted backend.
   */
  jsIfExpr?: string,
  /**
   * If `jsif` was present, this is the executable version of the same.
   */
  if?: (args: ClientFuncArgBag) => boolean,
}

export interface Schema {
  sharedOptions: Record<string, {label: string, value: string}[]>,
  selectables: Selectable[],
  filterables: Filterable[],
  /**
   * list of names of filterables that must be filled out by the user.
   * They are to be "and'd" at the top-most level of the query.
   */
  requiredTopLevelAnds: string[],
  primaryKeyCol: string,
  bulkOps: BulkOpDef[]
}

export interface BulkOpDef {
  keyCol: string, // e.g. `registrationID`, meaning "PK for each row is found in the registrationID column"
  keyType: string, // e.g. "registration", meaning "PKs represent registration objects"
  label: string,
  url: string,
}

export interface ReportLabelDef {
  label: string,
  colDataType:
    | "string"
    | "date"
    | "datetime"
    | "money" // money is expected to be in USD and be a number (not a string), e.g. 1.50 is "one dollar and fifty cents"
    | "bit-bool"
    | "number"
    | "hyperlink"
    ,
  columnGroupPosition: Integerlike,
  columnGroup: Integerlike,
}

export type ColumnToLabelMapping = {[resultColName: string]: ReportLabelDef};

export async function getSchemaRoots(axios: AxiosInstance) : Promise<{value: string, label: string}[]> {
  const response = await axios.get(`v1/reportBuilder/getAvailableSchemaRoots`)
  return response.data.data;
}

export async function getSchema(axios: AxiosInstance, queryRoot: "user" | (string & {})) : Promise<Schema> {
  const response = await axios.get(`v1/reportBuilder/getSchema`, {params: {queryRoot}})
  const schema = response.data.data as Schema;
  schema.filterables.forEach(filterable => {
    if (filterable.type === "functionlike") {
      if (filterable.jsToStringExpr) {
        try {
          const f : Filterable["getAsString"] = new Function("filterable", "args", `"use strict"; return ${filterable.jsToStringExpr}`) as any;
          if (process.env.NODE_ENV === "development") {
            filterable.getAsString = (filterable: any, args: any) => {
              try {
                return f!(filterable, args)
              }
              catch (e) {
                debugger;
                throw e;
              }
            }
          }
          else {
            filterable.getAsString = f;
          }
        }
        catch {
          if (process.env.NODE_ENV === "development") {
            throw Error(`filterable for target '${filterable.name}' failed to parse jsToStringExpr '${filterable.jsToStringExpr}'`)
          }
        }
      }
      filterable.argsDef.forEach(argDef => {
        if (argDef.jsIfExpr) {
          try {
            const f : Filterable_ArgDef["if"] = new Function("args", `"use strict"; return ${argDef.jsIfExpr}`) as any;
            if (process.env.NODE_ENV === "development") {
              argDef.if = (args: any) => {
                try {
                  return f!(args)
                }
                catch (e) {
                  debugger;
                  throw e;
                }
              }
            }
            else {
              argDef.if = f
            }
          }
          catch {
            if (process.env.NODE_ENV === "development") {
              throw Error(`filterable for target '${filterable.name}' failed to parse jsIfExpr '${argDef.jsIfExpr}'`)
            }
          }
        }
      })
    }
    else {
      // no-op
    }
  })

  return schema;
}

export type RowData = Record<string, any> & {__rowDataBrand__: never}

export interface QueryResult {
  queryResult: RowData[],
  labels: ColumnToLabelMapping,
  /**
   * only present if user is inLeague=1
   */
  __debug__sql?: string,
}

export async function runQuery(axios: AxiosInstance, args: {query: SerializedQueryRequest, dryRun?: boolean}) : Promise<QueryResult> {
  const response = await axios.post(`v1/reportBuilder/runQuery`, {query: args.query, dryRun: args.dryRun})
  return response.data.data;
}

export async function listQueries(ax: AxiosInstance, args: {coreEntity: string, userID: Guid | "ALL"}) : Promise<{queryID: Guid, queryName: string}[]> {
  const response = await ax.get(`V1/reportBuilder/listQueries`, {params: {coreEntity: args.coreEntity, userID: args.userID}});
  return response.data.data;
}

export async function getQuery(ax: AxiosInstance, args: {queryID: Guid}) : Promise<{query: SerializedQueryRequest}> {
  const response = await ax.get(`v1/reportBuilder/getQuery`, {params: {queryID: args.queryID}});
  return response.data.data;
}

export type SerializedQueryRequest = {
  coreEntity: string,
  select: {[name: string]: RequestSelectable},
  where: RequestWhereClause,
}

// TODO: maybe call "ServerSelectable"
export interface RequestSelectable {
  args: {
    [argName: string]: RequestArg
  },
  columnGroup: Integerlike,
  columnGroupPosition: Integerlike,
}

// TODO: maybe call "ServerWhereClause"
export type RequestWhereClause =
  | {or: (RequestWhereClause | RequestWhereArgs)[], and?: never}
  | {and: (RequestWhereClause | RequestWhereArgs)[], or?: never}

/**
 * It is required that each "FunctionlikeWhereArgs" object has exactly 1 top-level key,
 * representing the name of the functionlike it is invoking
 */
export type RequestWhereArgs = {
  [filterableName: string]: {[argName: string]: RequestArg}
}

export type RequestArg =
  | null
  | string
  | string[]
  | {[name: string]: string}

/**
 * for some filterable, this should contain values for all args for that filterable
 */
export type ClientFuncArgBag = Record<
  string, // arg name
  | null // got if'd away, there is no value for the corresponding arg
  | {label: string, value: string} // radio, text, date, anything with 1 thing, labels are also shuttled through for things with options
  | {label: string, value: string}[] // select-multi, checkboxes, things with multiple options
  | {[treeNodeName: string]: {label: string, value: string}} // menu-tree
>
